import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card } from "react-bootstrap"
import { useTranslation } from "react-i18next";
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Panel, PanelType } from '@fluentui/react/lib/Panel';
import { useBoolean } from '@fluentui/react-hooks';
import { Dialog, DialogFooter, DialogType } from '@fluentui/react/lib/Dialog';
import { __MessageTypes, enum_OBLIGATION_FORM_VIEW, __TimeScale, __SimpleYesNo, enum__TimeScale, __DueDayRule, enum__DueDayRule } from '../../../common/Dictionaries';
import PropTypes from 'prop-types';
import JqxInput from 'jqwidgets-scripts/jqwidgets-react-tsx/jqxinput';
import JqxNumberInput from 'jqwidgets-scripts/jqwidgets-react-tsx/jqxnumberinput';
import { getDueDayRule, isNullUndefined, isNullUndefinedOrEmpty, isNullUndefinedOrEmptyOrNegative, isNullUndefinedOrEmptyOrZero, nestedCopy, showMessage, treeify } from '../../../common/Helpers';
import { ChoiceGroup, Dropdown, SpinnerSize, TooltipHost } from '@fluentui/react';
import CLabel from '../../../components/Common/CLabel';
import CValidator from '../../../components/Common/CValidator';
import CTextAreaSimple from '../../../components/Common/CTextAreaSimple';
import MessageBoxInfoComplex from '../../../components/Common/Message/MessageBoxInfoComplex';
import Loading from '../../../components/Templates/Site/Loading';
import { useGetObligationCategorysMaintainer, useGetObligationListRolesByCategory, useGetObligationSubLegalEntities } from '../../../hooks/services/sCO/useOfObligacion';
import { __OBJECT_STRUCTURE_Obligation } from '../../../common/ObjectStructures/sCO';
import { setObligacion, updateObligacion, setObligationSubLegalEntities } from '../../../services/sCO/obligation.service';
import { getAllLE } from '../../../services/sBook/legalEntity.service';
import { cloneDeep } from 'lodash-es';
import Picker from '../../../components/Common/Picker';

// Ids fijos de core.legal_entity_type, iguales en todas las bases de clientes.
// 1 = Matriz/OWNER (siempre una sola por cliente); 2 = Sub LE/Filial/Área (código LETSFILIAL).
const MATRIZ_LEGAL_ENTITY_TYPE_ID = 1;
const SUB_LE_LEGAL_ENTITY_TYPE_ID = 2;

const SidePanelAddNew = (props) => {
    const { t, i18n } = useTranslation();

    // El servidor responde con una clave de idioma; si esa clave no está traducida se muestra el error genérico en vez de la clave cruda.
    const tServerMessage = (message) => (!isNullUndefinedOrEmpty(message) && i18n.exists(message)) ? t(message) : t('data-lang-msg-operationerror');
   
    const {
        inputParam_IsPanelOpen = null,
        inputParam_SetterIsPanelOpen = null,
        inputParam_ItemToEdit: inputParam_ItemToEditSource = null,
        inputParam_SetItemEdit = null,
        inputParam_SetterLisObligations = null,
        inputParam_LisObligations = null,
        inputParam_FormView = enum_OBLIGATION_FORM_VIEW.VIEW,
    } = props;

    // El panel edita una copia: inputParam_ItemToEditSource es el mismo objeto que vive en el listado del
    // padre, y mutarlo directo hacía que la grilla mostrara cambios sin guardar (incluso tras cerrar el panel).
    // La copia se vuelca al listado solo cuando el guardado resulta exitoso (ver onSave).
    const itemDraftRef = useRef(null);
    if (itemDraftRef.current === null && inputParam_ItemToEditSource !== null) {
        itemDraftRef.current = cloneDeep(inputParam_ItemToEditSource);
    }
    const inputParam_ItemToEdit = itemDraftRef.current;

    // La copia puede venir de listObligation (respuesta del backend, sin subLegalEntityIds)
    // en vez de __OBJECT_STRUCTURE_Obligation (que sí lo trae) cuando se edita una obligación existente.
    if (inputParam_ItemToEdit !== null && inputParam_ItemToEdit.subLegalEntityIds === undefined) {
        inputParam_ItemToEdit.subLegalEntityIds = [];
    }

    const [currentFormView, setCurrentFormView] = useState(inputParam_FormView);
    // Se incrementa en "Limpiar" para remontar el formulario: ChoiceGroup (defaultSelectedKey) y JqxInput
    // no se re-sincronizan solos con el objeto mutado.
    const [formResetKey, setFormResetKey] = useState(0);
    const horizontalChoiceDirection = { flexContainer: { display: "flex", flexDirection: "row", gap: 16 } };

    const [listCategories, setListCategories] = useState(null);
    useGetObligationCategorysMaintainer(listCategories, setListCategories)

    // "Impedir aprobación ADN" solo se puede marcar si la categoría es de EP (esEdp). En una categoría que no lo es
    // queda en No y deshabilitado; un valor Sí antiguo se corrige a No al abrir el formulario (si no, el guardado se rechaza).
    const isCategoriaEsEdp = (categoriaId) => (listCategories || []).find(c => c.id === categoriaId)?.esEdp === true;
    const categoriaEsEdp = isCategoriaEsEdp(inputParam_ItemToEdit.categoriaId);

    useEffect(() => {
        if (listCategories !== null && currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW
            && inputParam_ItemToEdit.impedirAprobacionAdn === true && !isCategoriaEsEdp(inputParam_ItemToEdit.categoriaId)) {
            inputParam_ItemToEdit.impedirAprobacionAdn = false;
            setFormResetKey(prevKey => prevKey + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listCategories]);

    const [listRolResponsableCumplimiento, setListRolResponsableCumplimiento] = useState(null);
    useGetObligationListRolesByCategory(inputParam_ItemToEdit.categoriaId, listRolResponsableCumplimiento, setListRolResponsableCumplimiento)

    const [listRolResponsableValidacion, setListRolResponsableValidacion] = useState(null);
    useGetObligationListRolesByCategory(inputParam_ItemToEdit.categoriaId, listRolResponsableValidacion, setListRolResponsableValidacion)

    // Catálogo de Legal Entities para el picker (Matriz + Área/Sub-LE): en edición se trae desde el backend
    // (ya viene con existeObligacion marcando las vinculadas); en creación (id === 0) ese endpoint no aplica,
    // se arma desde el catálogo completo de LE.
    const [subLegalEntityCatalog, setSubLegalEntityCatalog] = useState(null);
    useGetObligationSubLegalEntities(inputParam_ItemToEdit.id, subLegalEntityCatalog, setSubLegalEntityCatalog);

    useEffect(() => {
        const getInsertModeSubLegalEntityCatalog = async () => {
            try {
                const serviceResponse = await getAllLE();
                const response = serviceResponse.data;

                if (response.succeeded) {
                    setSubLegalEntityCatalog(response.data
                        .filter(x => x.legalEntityTypeId === MATRIZ_LEGAL_ENTITY_TYPE_ID || x.legalEntityTypeId === SUB_LE_LEGAL_ENTITY_TYPE_ID)
                        .map(x => ({ legalEntityId: x.id, legalEntityTypeId: x.legalEntityTypeId, legalEntityCode: x.legalEntityCode, legalEntityName: x.name, existeObligacion: false })));
                }
                else {
                    setSubLegalEntityCatalog([]);
                }
            }
            catch (error) {
                setSubLegalEntityCatalog([]);
            }
        };

        if (isNullUndefinedOrEmptyOrZero(inputParam_ItemToEdit.id) && subLegalEntityCatalog === null) {
            getInsertModeSubLegalEntityCatalog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputParam_ItemToEdit.id, subLegalEntityCatalog]);

    // Al llegar el catálogo por primera vez (modo edición), semilla subLegalEntityIds con lo ya vinculado.
    useEffect(() => {
        if (subLegalEntityCatalog !== null && inputParam_ItemToEdit.subLegalEntityIds.length === 0) {
            const linkedIds = subLegalEntityCatalog.filter(x => x.existeObligacion).map(x => x.legalEntityId);

            if (linkedIds.length > 0) {
                inputParam_ItemToEdit.subLegalEntityIds = linkedIds;
                setSubLegalEntityCatalog([...subLegalEntityCatalog]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subLegalEntityCatalog]);

    // Jerarquía visual (sin relación real en BD): la única Matriz (tipo 1) del cliente es la raíz,
    // todas las Área/Sub-LE (tipo 2) son sus hijas. Ambas quedan seleccionables.
    const subLegalEntityPickerData = subLegalEntityCatalog !== null
        ? (() => {
            const matrizItem = subLegalEntityCatalog.find(x => x.legalEntityTypeId === MATRIZ_LEGAL_ENTITY_TYPE_ID);
            return treeify(
                subLegalEntityCatalog.map(x => ({
                    id: x.legalEntityId,
                    code: x.legalEntityCode,
                    name: x.legalEntityName,
                    parentId: (x.legalEntityTypeId === SUB_LE_LEGAL_ENTITY_TYPE_ID && matrizItem) ? matrizItem.legalEntityId : null,
                    checked: inputParam_ItemToEdit.subLegalEntityIds.includes(x.legalEntityId),
                })),
                'id', 'parentId', 'children', 'code', 'name'
            );
          })()
        : [];

    // La librería (react-dropdown-tree-select), al marcar un nodo padre, no incluye sus hijos en
    // "selectedNodes" (solo agrega el propio nodo padre como tag) — hay que forzar la cascada a mano:
    // marcar/desmarcar la Matriz marca/desmarca también todas las Área actuales.
    const onChangeSubLegalEntity = (currentNode, selectedNodes) => {
        const matrizItem = (subLegalEntityCatalog || []).find(x => x.legalEntityTypeId === MATRIZ_LEGAL_ENTITY_TYPE_ID);
        const isMatrizNode = matrizItem !== undefined && currentNode.value === matrizItem.legalEntityId;

        let newIds = selectedNodes.map(n => n.value);

        if (isMatrizNode) {
            const allAreaIds = (subLegalEntityCatalog || [])
                .filter(x => x.legalEntityTypeId === SUB_LE_LEGAL_ENTITY_TYPE_ID)
                .map(x => x.legalEntityId);

            newIds = currentNode.checked
                ? Array.from(new Set([...newIds, ...allAreaIds]))
                : newIds.filter(id => !allAreaIds.includes(id));
        }

        inputParam_ItemToEdit.subLegalEntityIds = newIds;
        setSubLegalEntityCatalog(prevList => prevList.map(x => x.legalEntityId === currentNode.value ? { ...x, existeObligacion: currentNode.checked } : x));
    };

    const [disableForm, setDisableForm] = useState(false);
    const [isDialogVisible, { setTrue: showDialog, setFalse: hideDialog }] = useBoolean(false);
    const [messageConfig, setMessageConfig] = useState({
        message : null,
        messageType : "",
        showMessage : false,
    });
    const [objIsValidForm, setObjIsValidForm] = useState({
        isInValidCategoria                   : false,
        isInValidNombre                      : false,
        isInValidNombreNemo                  : false,
        isInValidDescripcion                 : false,
        isInValidPeriodica                   : false,
        isInValidPeriodicidad                : false,
        isInValidPeriodoAlertaDias           : false,
        isInValidEscalaTiempo                : false,
        isInValidrespertarUbicacionReferencia: false,
        isInValidPostergarVencimiento        : false,
        isInValidMultaIncumplimiento         : false,
        isInValidClausulaMulta               : false,
        isInValidImpedirAprobacionAdn        : false,
        isInValidRequiereDocumento           : false,
        isInValidSujetaAValidacion           : false,
        isInValidRolresponsableCumplimiento  : false,
        isInValidRolresponsablevalidacion    : false,

        isInValidRoles_SameRolesNotAlowed    : false,
    });

    // Al cambiar de categoría la lista de roles puede ser otra y el rol ya elegido puede no corresponder: sin esto se guardaba igual (p. ej. un rol de Book en una
    // obligación de EP). Solo actúa tras un cambio de categoría hecho en este formulario; al abrir una obligación existente no toca sus roles.
    const categoriaCambiadaRef = useRef(false);

    useEffect(() => {
        if (!categoriaCambiadaRef.current || listRolResponsableCumplimiento === null || listRolResponsableValidacion === null) {
            return;
        }

        categoriaCambiadaRef.current = false;
        let huboCambios = false;

        if (!isNullUndefinedOrEmpty(inputParam_ItemToEdit.rolResponsableCumplimiento) && !listRolResponsableCumplimiento.some(r => r.role === inputParam_ItemToEdit.rolResponsableCumplimiento)) {
            inputParam_ItemToEdit.rolResponsableCumplimiento = '';
            inputParam_ItemToEdit.rolClienteResponsableCumplimiento = '';
            setObjIsValidForm(prevState => ({ ...prevState, isInValidRolresponsableCumplimiento: true }));
            huboCambios = true;
        }

        if (!isNullUndefinedOrEmpty(inputParam_ItemToEdit.rolResponsableValidacion) && !listRolResponsableValidacion.some(r => r.role === inputParam_ItemToEdit.rolResponsableValidacion)) {
            inputParam_ItemToEdit.rolResponsableValidacion = '';
            inputParam_ItemToEdit.rolClienteResponsableValidacion = '';
            setObjIsValidForm(prevState => ({ ...prevState, isInValidRolresponsablevalidacion: true }));
            huboCambios = true;
        }

        if (huboCambios) {
            setFormResetKey(prevKey => prevKey + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listRolResponsableCumplimiento, listRolResponsableValidacion]);

    const isValidForm = () => {
        let isInvalid = false;

        objIsValidForm.isInValidCategoria = isNullUndefinedOrEmptyOrNegative(inputParam_ItemToEdit.categoriaId) || inputParam_ItemToEdit.categoriaId === 0;
        isInvalid = !isInvalid ? objIsValidForm.isInValidCategoria : isInvalid;

        objIsValidForm.isInValidNombre = isNullUndefinedOrEmpty(inputParam_ItemToEdit.nombre);
        isInvalid = !isInvalid ? objIsValidForm.isInValidNombre : isInvalid;

        objIsValidForm.isInValidNombreNemo = isNullUndefinedOrEmpty(inputParam_ItemToEdit.nombreNemo);
        isInvalid = !isInvalid ? objIsValidForm.isInValidNombreNemo : isInvalid;

        objIsValidForm.isInValidDescripcion = isNullUndefinedOrEmpty(inputParam_ItemToEdit.descripcion);
        isInvalid = !isInvalid ? objIsValidForm.isInValidDescripcion : isInvalid;

        if (inputParam_ItemToEdit.periodica === true) {
            objIsValidForm.isInValidPeriodicidad = isNullUndefined(inputParam_ItemToEdit.periodicidadMeses) || inputParam_ItemToEdit.periodicidadMeses === 0;
            isInvalid = !isInvalid ? objIsValidForm.isInValidPeriodicidad : isInvalid;

            objIsValidForm.isInValidEscalaTiempo = isNullUndefined(inputParam_ItemToEdit.escalaTiempo);
            isInvalid = !isInvalid ? objIsValidForm.isInValidEscalaTiempo : isInvalid;
        }
        else {
            objIsValidForm.isInValidPeriodicidad = false;
            objIsValidForm.isInValidEscalaTiempo = false;
        }

        objIsValidForm.isInValidPeriodoAlertaDias = isNullUndefinedOrEmptyOrNegative(inputParam_ItemToEdit.periodoAlerta);
        isInvalid = !isInvalid ? objIsValidForm.isInValidPeriodoAlertaDias : isInvalid;
        
        // Mensual: hay que elegir el «Día de vencimiento» (su validador conserva el nombre de «Respetar ubicación», que hoy es una de sus respuestas).
        if (inputParam_ItemToEdit.escalaTiempo === enum__TimeScale.MONTHS) {
            objIsValidForm.isInValidrespertarUbicacionReferencia = isNullUndefined(getDueDayRule(inputParam_ItemToEdit));
            isInvalid = !isInvalid ? objIsValidForm.isInValidrespertarUbicacionReferencia : isInvalid;
        }
        else {
            objIsValidForm.isInValidrespertarUbicacionReferencia = false;
        }

        objIsValidForm.isInValidCumplirAntesVencimiento = isNullUndefined(inputParam_ItemToEdit.cumplirAntesVencimiento);
        isInvalid = !isInvalid ? objIsValidForm.isInValidCumplirAntesVencimiento : isInvalid;

        objIsValidForm.isInValidPostergarVencimiento = isNullUndefined(inputParam_ItemToEdit.postergarVencimiento);
        isInvalid = !isInvalid ? objIsValidForm.isInValidPostergarVencimiento : isInvalid;

        objIsValidForm.isInValidMultaIncumplimiento = isNullUndefined(inputParam_ItemToEdit.multaIncumplimiento);
        isInvalid = !isInvalid ? objIsValidForm.isInValidMultaIncumplimiento : isInvalid;
        
        if (inputParam_ItemToEdit.multaIncumplimiento === true) {
            objIsValidForm.isInValidClausulaMulta = isNullUndefinedOrEmpty(inputParam_ItemToEdit.clausulaMulta);
            isInvalid = !isInvalid ? objIsValidForm.isInValidClausulaMulta : isInvalid;
        }
        else {
            objIsValidForm.isInValidClausulaMulta = false;
        }
        
        //objIsValidForm.isInValidImpedirAprobacionAdn = isNullUndefined(inputParam_ItemToEdit.impedirAprobacionAdn);
        //isInvalid = !isInvalid ? objIsValidForm.isInValidImpedirAprobacionAdn : isInvalid;

        objIsValidForm.isInValidRequiereDocumento = isNullUndefined(inputParam_ItemToEdit.requiereDocumento);
        isInvalid = !isInvalid ? objIsValidForm.isInValidRequiereDocumento : isInvalid;
        
        objIsValidForm.isInValidSujetaAValidacion = isNullUndefined(inputParam_ItemToEdit.sujetaAValidacion);
        isInvalid = !isInvalid ? objIsValidForm.isInValidSujetaAValidacion : isInvalid;

        objIsValidForm.isInValidRolresponsableCumplimiento = isNullUndefinedOrEmpty(inputParam_ItemToEdit.rolResponsableCumplimiento) || inputParam_ItemToEdit.rolResponsableCumplimiento === 0;
        isInvalid = !isInvalid ? objIsValidForm.isInValidRolresponsableCumplimiento : isInvalid;
        
        if (inputParam_ItemToEdit.sujetaAValidacion === true) {
            objIsValidForm.isInValidRolresponsablevalidacion = isNullUndefinedOrEmpty(inputParam_ItemToEdit.rolResponsableValidacion) || inputParam_ItemToEdit.rolResponsableValidacion === 0;
            isInvalid = !isInvalid ? objIsValidForm.isInValidRolresponsablevalidacion : isInvalid;

            if (objIsValidForm.isInValidRolresponsableCumplimiento === false && objIsValidForm.isInValidRolresponsablevalidacion === false) {
                if (inputParam_ItemToEdit.rolResponsableValidacion === inputParam_ItemToEdit.rolResponsableCumplimiento) {
                    objIsValidForm.isInValidRoles_SameRolesNotAlowed = true;
                    isInvalid = !isInvalid ? objIsValidForm.isInValidRoles_SameRolesNotAlowed : isInvalid;
                }
            }
        }
        else {
            objIsValidForm.isInValidRolresponsablevalidacion = false;
            objIsValidForm.isInValidRoles_SameRolesNotAlowed = false;
        }
        
        setObjIsValidForm({ ...objIsValidForm });

        return isInvalid
    };

    const onHideDialog = (ev) => {
        ev.preventDefault();
        inputParam_SetterIsPanelOpen(true);
        hideDialog();
    };

    const onDismissPanel = () => {
        inputParam_SetterIsPanelOpen(false);
        showMessage(setMessageConfig, false, null, null)
    };

    const onHideDialogAndPanel = () => {
        onDismissPanel();
        hideDialog();
    };   

    const onClosePanel = () => {
        onDismissPanel();
    };

    const onChangeObjObligacionTextInputNumber = (event, fieldName, isInvalidPropertyField) => {
        inputParam_ItemToEdit[fieldName] = event.args.value;
        setObjIsValidForm(prevState => ({ ...prevState, [isInvalidPropertyField]: isNullUndefinedOrEmpty(inputParam_ItemToEdit[fieldName]) }));
    };

    const onChangeObjObligacionTextInputString = (event, fieldName, isInvalidPropertyField) => {
        inputParam_ItemToEdit[fieldName] = event.currentTarget.value;
        setObjIsValidForm(prevState => ({ ...prevState, [isInvalidPropertyField]: isNullUndefinedOrEmpty(inputParam_ItemToEdit[fieldName]) }));
    };

    const onChangeObjObligacionTextAreaInputString = (control, fieldName) => {
        inputParam_ItemToEdit[fieldName] = control.currentTarget.value;
        setObjIsValidForm(prevState => ({ ...prevState, [fieldName === 'descripcion' ? 'isInValidDescripcion' : 'isInValidClausulaMulta']: isNullUndefinedOrEmpty(inputParam_ItemToEdit[fieldName]) }));
    };

    const onChangeObjObligacionChoice = (event, option, fieldName, isInvalidPropertyField) => {
        inputParam_ItemToEdit[fieldName] = option.key === 1;

        if (fieldName === "periodica") {
            if (inputParam_ItemToEdit[fieldName] === false) {
                inputParam_ItemToEdit['periodicidadMeses'] = null;
                inputParam_ItemToEdit['escalaTiempo'] = null;

                setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, ['isInValidPeriodicidad'] : false }));
                setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, ['isInValidEscalaTiempo'] : false }));
            }
        }

        if (fieldName === "multaIncumplimiento") {
            if (inputParam_ItemToEdit[fieldName] === false) {
                inputParam_ItemToEdit['clausulaMulta'] = null;
                setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, ['isInValidClausulaMulta'] : false }));
            }
        }

        setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, [isInvalidPropertyField] : isNullUndefined(inputParam_ItemToEdit[fieldName]) }));
    };

    // «Día de vencimiento» (mensual): una sola respuesta que se guarda en las dos marcas de la obligación.
    const onChangeObjObligacionDueDayRule = (event, option) => {
        inputParam_ItemToEdit.respertarUbicacionReferencia = option.key === enum__DueDayRule.SAME_WEEKDAY;
        inputParam_ItemToEdit.ultimoDiaMes = option.key === enum__DueDayRule.LAST_DAY_OF_MONTH;

        setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, isInValidrespertarUbicacionReferencia : false }));
    };

    const onChangeObjObligacionSelect = (event, option, fieldName, fieldNameClient, isInvalidPropertyField) => {
        inputParam_ItemToEdit[fieldName] = option.key;
        
        if (!isNullUndefined(fieldNameClient)) {
            inputParam_ItemToEdit[fieldNameClient] = option.text;
        }

        if (fieldName === "categoriaId") {
            inputParam_ItemToEdit['categoriaNombre'] = option.text;

            // La lista de roles depende de la categoría (las de Book usan roles de Book, las demás roles de contrato): se vuelve a pedir y, cuando llega,
            // se limpian los roles elegidos que ya no estén en la lista (efecto de categoriaCambiadaRef).
            categoriaCambiadaRef.current = true;
            setListRolResponsableCumplimiento(null);
            setListRolResponsableValidacion(null);

            // Al pasar a una categoría que no es de EP, "Impedir aprobación ADN" vuelve a No (se remonta el formulario para que el ChoiceGroup lo refleje).
            if (inputParam_ItemToEdit.impedirAprobacionAdn === true && !isCategoriaEsEdp(option.key)) {
                inputParam_ItemToEdit.impedirAprobacionAdn = false;
                setFormResetKey(prevKey => prevKey + 1);
            }
        }
        setObjIsValidForm && setObjIsValidForm(prevState => ({ ...prevState, [isInvalidPropertyField] : isNullUndefined(inputParam_ItemToEdit[fieldName]) || inputParam_ItemToEdit[fieldName] === 0 }));
    };

    // Vuelve el formulario a su estado en blanco (conserva solo el id). Como se trabaja sobre la copia,
    // no afecta la grilla hasta que se guarde.
    const onClean = () => {
        const cleanItem = __OBJECT_STRUCTURE_Obligation(inputParam_ItemToEdit.id);
        Object.keys(inputParam_ItemToEdit).forEach(key => delete inputParam_ItemToEdit[key]);
        Object.assign(inputParam_ItemToEdit, cleanItem);

        // Sin esto, el efecto que siembra subLegalEntityIds volvería a marcar lo que estaba vinculado.
        setSubLegalEntityCatalog(prevList => prevList === null ? prevList : prevList.map(x => ({ ...x, existeObligacion: false })));
        setObjIsValidForm(prevState => Object.keys(prevState).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
        showMessage(setMessageConfig, false, null, null);
        setFormResetKey(prevKey => prevKey + 1);
    };

    // FluentValidation (ej. NombreNemo con espacios/caracteres no permitidos) devuelve 400 con
    // { errors: { Campo: ["mensaje"] } }; sin esto siempre se mostraba el mensaje genérico y el
    // usuario no tenía forma de saber qué regla de validación falló.
    const getSaveErrorMessage = (error) => {
        const validationErrors = error?.response?.status === 400 ? error?.response?.data?.errors : null;
        if (isNullUndefinedOrEmpty(validationErrors)) {
            return t('data-lang-msg-operationerror');
        }
        return Object.values(validationErrors).flat().join(' ');
    };

    // La grilla pinta subLegalEntities (código/nombre), no subLegalEntityIds (solo ids, para el picker) —
    // sin esto, tras guardar, la fila mostraría el valor viejo hasta recargar la página.
    const buildSubLegalEntitiesDisplay = () => {
        return (subLegalEntityCatalog || [])
            .filter(x => inputParam_ItemToEdit.subLegalEntityIds.includes(x.legalEntityId))
            .map(x => ({ legalEntityId: x.legalEntityId, legalEntityCode: x.legalEntityCode, legalEntityName: x.legalEntityName }));
    };

    const saveSubLegalEntities = (obligacionId, targetItem) => {
        const itemsSubLE = inputParam_ItemToEdit.subLegalEntityIds.map(legalEntityId => ({ legalEntityId, existeObligacion: true }));
        return setObligationSubLegalEntities(obligacionId, itemsSubLE).then((serviceResponse) => {
            if (serviceResponse?.data?.succeeded === false) {
                // El servidor no guardó las Áreas (p. ej. se quitó un Área de la que dependen contratos vinculados): se recarga lo realmente guardado.
                inputParam_ItemToEdit.subLegalEntityIds = [];
                setSubLegalEntityCatalog(null);
                showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message));
                return;
            }
            targetItem.subLegalEntities = buildSubLegalEntitiesDisplay();
        }).catch(() => {
            showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-operationerror'))
        });
    };

    const onSave = () => {
        if (!isValidForm()) {
            setDisableForm(true);

            if (currentFormView === enum_OBLIGATION_FORM_VIEW.INSERT) {
                setObligacion(inputParam_ItemToEdit).then((serviceResponse) => {
                    if (serviceResponse.data.succeeded) {
                        const newItem = serviceResponse.data.data;
                        inputParam_SetItemEdit(newItem);
                        inputParam_LisObligations.push(newItem);

                        showMessage(setMessageConfig, true, __MessageTypes.SUCCESS, t('data-lang-msg-operationperformed'))
                        saveSubLegalEntities(newItem.id, newItem).finally(() => {
                            inputParam_SetterLisObligations && inputParam_SetterLisObligations(cloneDeep(inputParam_LisObligations));
                            setDisableForm(false);
                        });
                    }
                    else {
                        setDisableForm(false);
                        showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message))
                    }
                }).catch((error) => {
                    setDisableForm(false);
                    showMessage(setMessageConfig, true, __MessageTypes.ERROR, getSaveErrorMessage(error))
                });
            }
            else if (currentFormView === enum_OBLIGATION_FORM_VIEW.EDIT) {
                updateObligacion(inputParam_ItemToEdit.id, inputParam_ItemToEdit).then((serviceResponse) => {
                    if (serviceResponse.data.succeeded) {
                        inputParam_SetItemEdit(inputParam_ItemToEdit);
                        showMessage(setMessageConfig, true, __MessageTypes.SUCCESS, t('data-lang-msg-operationperformed'))
                        saveSubLegalEntities(inputParam_ItemToEdit.id, inputParam_ItemToEdit).finally(() => {
                            // Recién acá la grilla recibe los cambios: hasta ahora el panel trabajaba sobre una copia.
                            inputParam_SetterLisObligations && inputParam_SetterLisObligations(
                                inputParam_LisObligations.map(item => item.id === inputParam_ItemToEdit.id ? cloneDeep(inputParam_ItemToEdit) : item)
                            );
                            setDisableForm(false);
                        });
                    }
                    else {
                        setDisableForm(false);
                        showMessage(setMessageConfig, true, __MessageTypes.ERROR, tServerMessage(serviceResponse.data.message))
                    }
                }).catch((error) => {
                    setDisableForm(false);
                    showMessage(setMessageConfig, true, __MessageTypes.ERROR, getSaveErrorMessage(error))
                });
            }
        }
        else {
            showMessage(setMessageConfig, true, __MessageTypes.ERROR, t('data-lang-msg-requireddatanotset'))
        }
    };

    const onRenderFooterContent = () => {
        return  <Row>
                    <Col xl={12}>
                        <MessageBoxInfoComplex
                            inputParam_Message={ messageConfig.message }
                            inputParam_Display={ messageConfig.showMessage }
                            inputParam_MessageType={ messageConfig.messageType }
                            inputParam_SetterMessage={ setMessageConfig }
                            inputParam_TranslateText={true}
                            inputParam_ShowClose={true}
                        /> 
                    </Col>
                    {
                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                        <Col xl={12} className='d-flex'>
                                {
                                    !disableForm &&
                                    <>
                                        <PrimaryButton className='mx-1' onClick={ onSave }>{ t('data-lang-btn-save') }</PrimaryButton>
                                        <DefaultButton className='mx-1' onClick={ onClean }>{ t('data-lang-btn-clean') }</DefaultButton>
                                    </>
                                }
                                {
                                    disableForm &&
                                    <Loading
                                        inputParam_ShowLoading={true}
                                        inputParam_ShowText={false}
                                        inputParam_Width={'25px'}
                                        inputParam_Height={'25px'}
                                        inputParam_Left={""}
                                        inputParam_Style={{marginTop: 0}}
                                        inputParam_ColClassName={"me-2"}
                                        inputParam_MarginLeft={"auto"}
                                        inputParam_SpinnerSize={SpinnerSize.medium} />
                                }
                                <DefaultButton className='mx-1' onClick={ onClosePanel }>{ t('data-lang-btn-close') }</DefaultButton>
                        </Col>
                    }
                </Row>
    };

    return (
        <div>
            <Panel
                isOpen={ inputParam_IsPanelOpen }
                isLightDismiss={ true }
                onLightDismissClick={ showDialog }
                onDismiss={ onDismissPanel }
                headerText={ t(currentFormView === enum_OBLIGATION_FORM_VIEW.INSERT ? 'data-lang-boxname-obligationcreate' : (currentFormView === enum_OBLIGATION_FORM_VIEW.EDIT ? 'data-lang-boxname-obligationedit' : 'data-lang-boxname-obligationnew')) }
                closeButtonAriaLabel={ t('data-lang-btn-close') }
                isFooterAtBottom={ true }
                onRenderFooterContent={ onRenderFooterContent }
                customWidth={ PanelType.medium }
                type={ PanelType.medium }
            >
                {
                    inputParam_ItemToEdit && listCategories &&
                    <fieldset key={formResetKey} disabled={disableForm}>
                        <Card className='p-2 my-2'>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <CLabel labelText={ t('data-lang-lbl-category') } />
                                    <Dropdown
                                        placeholder={t('data-lang-lbl-category')}
                                        options={listCategories !== null ? listCategories.map(item => { return  { key: item.id, text: item.nombre } }) : []}
                                        selectedKey={inputParam_ItemToEdit.categoriaId}
                                        onChange={(event, option, index) => onChangeObjObligacionSelect(event, option, 'categoriaId', null, 'isInValidCategoria')}
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        checkable
                                        styles={ {title: { height: "27px" }} }
                                        className="mb-1"
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidCategoria} inValidText={t('data-lang-msg-required')} />
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <Picker
                                        data={ subLegalEntityPickerData }
                                        mode="multiSelect"
                                        className="dropdown-checkbox remove-position"
                                        texts={{ placeholder: t('data-lang-lbl-chooses') }}
                                        onChange={ onChangeSubLegalEntity }
                                        labelText={ t('data-lang-lbl-sublegalentity') }
                                        disabled={ currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW }
                                        refresh={true}
                                    />
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <CLabel labelText={ t('data-lang-lbl-name') } />
                                    <JqxInput
                                        height={25}
                                        width={"100%"}
                                        maxLength={60}
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        value={inputParam_ItemToEdit.nombre}
                                        onChange={(e) => onChangeObjObligacionTextInputString(e, 'nombre', 'isInValidNombre')}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidNombre} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <CLabel labelText={ t('data-lang-lbl-shortname') } />
                                    <JqxInput
                                        height={25}
                                        width={"100%"}
                                        maxLength={15}
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        value={inputParam_ItemToEdit.nombreNemo}
                                        onChange={(e) => onChangeObjObligacionTextInputString(e, 'nombreNemo', 'isInValidNombreNemo')}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidNombreNemo} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <CTextAreaSimple
                                        labelText={ t('data-lang-lbl-description') }
                                        editMode={ currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW }
                                        setterControlValue={ onChangeObjObligacionTextAreaInputString }
                                        setterControlIsDynamic={ true }
                                        setterField={'descripcion'}
                                        maxLength={450}
                                        controlValue={ inputParam_ItemToEdit.descripcion }
                                        isInvalirControl={ objIsValidForm.isInValidDescripcion }
                                        rows={8}
                                    />
                                </Col>
                            </Row>
                        </Card>
                        <Card className='p-2 my-2'>
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-periodica') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.periodica !== null ? (inputParam_ItemToEdit.periodica ? 1 : 0) : 0}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'periodica', 'isInValidPeriodica')}
                                        required={true}
                                    />
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <Row>
                                        <Col>
                                            <Row>
                                                <Col>
                                                    <CLabel labelText={ t('data-lang-lbl-periodicidad') } />
                                                </Col>
                                                <Col>
                                                    <TooltipHost
                                                        content={t('data-lang-msg-selectperiod')}
                                                        calloutProps={{ gapSpace: 0 }}
                                                    >
                                                        <Dropdown
                                                            placeholder={t('data-lang-lbl-select')}
                                                            options={ nestedCopy(__TimeScale).map(element => {
                                                                element.text = t(element.value);
                                                                return element
                                                            }) } 
                                                            selectedKey={inputParam_ItemToEdit.escalaTiempo}
                                                            onChange={(event, option, index) => onChangeObjObligacionSelect(event, option, 'escalaTiempo', null, 'isInValidEscalaTiempo')}
                                                            disabled={(!inputParam_ItemToEdit.periodica || currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW)}
                                                            checkable
                                                            styles={ {title: { height: "27px" }} }
                                                            className="mb-1"
                                                        />
                                                    </TooltipHost>
                                                    {
                                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                                        <div>
                                                            <CValidator isInValid={objIsValidForm.isInValidEscalaTiempo} inValidText={t('data-lang-msg-required')} /> 
                                                        </div>
                                                    }
                                                </Col>
                                            </Row>
                                            <Row className='pt-1'>
                                                <Col>
                                                    <CLabel labelText={ t('data-lang-lbl-alertperioddais') } />
                                                </Col>
                                                <Col>
                                                    <JqxNumberInput
                                                        decimalDigits={0}
                                                        min={0}
                                                        spinButtons={false}
                                                        max={100}
                                                        inputMode="simple"
                                                        width={80}
                                                        height={25}
                                                        symbolPosition={'right'}
                                                        digits={3}
                                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                                        value={!isNullUndefined(inputParam_ItemToEdit.periodoAlerta) ? inputParam_ItemToEdit.periodoAlerta : 0}
                                                        onValueChanged={(e) => onChangeObjObligacionTextInputNumber(e, 'periodoAlerta', 'isInValidPeriodoAlertaDias')}
                                                    />
                                                    {
                                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                                        <div>
                                                            <CValidator isInValid={objIsValidForm.isInValidPeriodoAlertaDias} isInvalidText={!objIsValidForm.isInValidPeriodoAlertaDias ? "" : null} inValidText={t('data-lang-msg-required')} />
                                                        </div>
                                                    }
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col>
                                            <Row>
                                                <Col>
                                                    <TooltipHost
                                                        content={t('data-lang-msg-timeperiod')}
                                                        calloutProps={{ gapSpace: 0 }}
                                                    >
                                                        <JqxNumberInput
                                                            decimalDigits={0}
                                                            min={0}
                                                            spinButtons={false}
                                                            max={100}
                                                            inputMode="simple"
                                                            width={80}
                                                            height={25}
                                                            symbolPosition={'right'}
                                                            digits={3}
                                                            disabled={(!inputParam_ItemToEdit.periodica || currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW)}
                                                            value={!isNullUndefined(inputParam_ItemToEdit.periodicidadMeses) ? inputParam_ItemToEdit.periodicidadMeses : 0}
                                                            onValueChanged={(e) => onChangeObjObligacionTextInputNumber(e, 'periodicidadMeses', 'isInValidPeriodicidad')}
                                                        />
                                                    </TooltipHost>
                                                    {
                                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                                        <div>
                                                            <CValidator isInValid={objIsValidForm.isInValidPeriodicidad} isInvalidText={!objIsValidForm.isInValidPeriodicidad ? "" : null} inValidText={t('data-lang-msg-required')} />
                                                        </div>
                                                    }
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            {
                                inputParam_ItemToEdit.escalaTiempo === enum__TimeScale.MONTHS &&
                                <Row className='pt-1'>
                                    <Col xs={12} xl={12}>
                                        <CLabel labelText={ t('data-lang-lbl-duedayrule') } />
                                    </Col>
                                    <Col xs={12} xl={12}>
                                        <ChoiceGroup
                                            defaultSelectedKey={getDueDayRule(inputParam_ItemToEdit)}
                                            options={ __DueDayRule.map(element => ({ key: element.key, text: `${t(element.value)} (${t(element.example)})` })) }
                                            disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                            onChange={onChangeObjObligacionDueDayRule}
                                            required={true}
                                        />
                                        <div className="text-muted small">{ t('data-lang-msg-duedayrule-help') }</div>
                                        {
                                            currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                            <div>
                                                <CValidator isInValid={objIsValidForm.isInValidrespertarUbicacionReferencia} inValidText={t('data-lang-msg-required')} />
                                            </div>
                                        }
                                    </Col>
                                </Row>
                            }
                            
                        </Card>
                        <Card className='p-2 my-2'>
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-canachievebeforeduedate') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.cumplirAntesVencimiento !== null ? (inputParam_ItemToEdit.cumplirAntesVencimiento ? 1 : 0) : null}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'cumplirAntesVencimiento', 'isInValidCumplirAntesVencimiento')}
                                        required={true}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidCumplirAntesVencimiento} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-postponeexpiration') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.postergarVencimiento !== null ? (inputParam_ItemToEdit.postergarVencimiento ? 1 : 0) : null}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'postergarVencimiento', 'isInValidPostergarVencimiento')}
                                        required={true}
                                    />
                                    <div className="text-muted small">{ t('data-lang-msg-postponeexpiration-help') }</div>
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidPostergarVencimiento} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-finesnoncompliance') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.multaIncumplimiento !== null ? (inputParam_ItemToEdit.multaIncumplimiento ? 1 : 0) : null}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'multaIncumplimiento', 'isInValidMultaIncumplimiento')}
                                        required={true}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidMultaIncumplimiento} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            {
                                inputParam_ItemToEdit.multaIncumplimiento === true &&
                                <Row className='pt-1'>
                                    <Col xs={12} xl={12} className="mt-1">
                                        <CTextAreaSimple
                                            labelText={ t('data-lang-lbl-clausulamulta') }
                                            editMode={ inputParam_ItemToEdit.multaIncumplimiento === true && currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW }
                                            setterControlValue={ onChangeObjObligacionTextAreaInputString }
                                            setterControlIsDynamic={ true }
                                            setterField={'clausulaMulta'}
                                            maxLength={450}
                                            controlValue={ inputParam_ItemToEdit.clausulaMulta }
                                            isInvalirControl={ inputParam_ItemToEdit.multaIncumplimiento === true ? false : objIsValidForm.isInValidClausulaMulta }
                                            rows={8}
                                        />
                                    </Col>
                                </Row>
                            }
                            {
                                <Row className='pt-1'>
                                    <Col>
                                        <CLabel labelText={ t('data-lang-lbl-preventadnapproval') } />
                                    </Col>
                                    <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                        <ChoiceGroup
                                            styles={horizontalChoiceDirection}
                                            defaultSelectedKey={inputParam_ItemToEdit.impedirAprobacionAdn !== null ? (inputParam_ItemToEdit.impedirAprobacionAdn ? 1 : 0) : null}
                                            options={ nestedCopy(__SimpleYesNo).map(element => {
                                                    element.text = t(element.value);
                                                    return element
                                            }) }
                                            disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW || !categoriaEsEdp}
                                            onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'impedirAprobacionAdn', 'isInValidImpedirAprobacionAdn')}
                                            required={true}
                                        />
                                        {
                                            currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW && !categoriaEsEdp &&
                                            <div className="text-muted" style={{ fontSize: 12 }}>{t('data-lang-msg-obligation-impedir-requiresedp-hint')}</div>
                                        }
                                        {
                                            currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                            <div>
                                                <CValidator isInValid={objIsValidForm.isInValidImpedirAprobacionAdn} inValidText={t('data-lang-msg-required')} />
                                            </div>
                                        }
                                    </Col>
                                </Row>
                            }
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-requiredattacheddocument') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.requiereDocumento !== null ? (inputParam_ItemToEdit.requiereDocumento ? 1 : 0) : null}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'requiereDocumento', 'isInValidRequiereDocumento')}
                                        required={true}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidRequiereDocumento} inValidText={t('data-lang-msg-required')} />
                                        </div>
                                    }
                                </Col>
                            </Row>
                            {
                                // Visible para cualquier cliente (lo administran los Admin/sAdmin); solo la
                                // funcionalidad del checklist en sí (que consume este flag) es exclusiva de AMSA.
                                <Row className='pt-1'>
                                    <Col>
                                        <CLabel labelText={'Incluir en checklist EdP'} />
                                    </Col>
                                    <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                        <ChoiceGroup
                                            styles={horizontalChoiceDirection}
                                            defaultSelectedKey={inputParam_ItemToEdit.incluirEnChecklistEdp !== null ? (inputParam_ItemToEdit.incluirEnChecklistEdp ? 1 : 0) : null}
                                            options={ nestedCopy(__SimpleYesNo).map(element => {
                                                    element.text = t(element.value);
                                                    return element
                                            }) }
                                            disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                            onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'incluirEnChecklistEdp', 'isInValidIncluirEnChecklistEdp')}
                                        />
                                    </Col>
                                </Row>
                            }
                        </Card>
                        <Card className='p-2 my-2'>
                            <Row className='pt-1'>
                                <Col>
                                    <CLabel labelText={ t('data-lang-lbl-obligationreqvalidation') } />
                                </Col>
                                <Col xs={6} xl={6} style={{marginTop: "-5px"}}>
                                    <ChoiceGroup
                                        styles={horizontalChoiceDirection}
                                        defaultSelectedKey={inputParam_ItemToEdit.sujetaAValidacion !== null ? (inputParam_ItemToEdit.sujetaAValidacion ? 1 : 0) : null}
                                        options={ nestedCopy(__SimpleYesNo).map(element => {
                                                element.text = t(element.value);
                                                return element
                                        }) }
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        onChange={(ev, option) => onChangeObjObligacionChoice(ev, option, 'sujetaAValidacion', 'isInValidSujetaAValidacion')}
                                        required={true}
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidSujetaAValidacion} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            <Row className='pt-1'>
                                <Col xs={12} xl={12} className="mt-1">
                                    <CLabel labelText={ t('data-lang-lbl-responsibleroleachieve') } />
                                    <Dropdown
                                        placeholder={t('data-lang-lbl-responsibleroleachieve')}
                                        options={listRolResponsableCumplimiento !== null ? listRolResponsableCumplimiento.map(item => { return  { key: item.role, text: item.roleClient } }) : []}
                                        selectedKey={inputParam_ItemToEdit.rolResponsableCumplimiento}
                                        onChange={(event, option, index) => onChangeObjObligacionSelect(event, option, 'rolResponsableCumplimiento', 'rolClienteResponsableCumplimiento', 'isInValidRolresponsableCumplimiento')}
                                        disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                        checkable
                                        styles={ {title: { height: "27px" }} }
                                        className="mb-1"
                                    />
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidRolresponsableCumplimiento} inValidText={t('data-lang-msg-required')} /> 
                                        </div>
                                    }
                                    {
                                        currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                        <div>
                                            <CValidator isInValid={objIsValidForm.isInValidRoles_SameRolesNotAlowed} inValidText={t('data-lang-msg-responsiblerolachievevalidatesamenotalowed')} /> 
                                        </div>
                                    }
                                </Col>
                            </Row>
                            {
                                inputParam_ItemToEdit.sujetaAValidacion === true &&
                                <Row className='pt-1'>
                                    <Col xs={12} xl={12} className="mt-1">
                                        <CLabel labelText={ t('data-lang-lbl-responsiblerolevalidation') } />
                                        <Dropdown
                                            placeholder={t('data-lang-lbl-responsiblerolevalidation')}
                                            options={listRolResponsableValidacion !== null ? listRolResponsableValidacion.map(item => { return  { key: item.role, text: item.roleClient } }) : []}
                                            selectedKey={inputParam_ItemToEdit.rolResponsableValidacion}
                                            onChange={(event, option, index) => onChangeObjObligacionSelect(event, option, 'rolResponsableValidacion', 'rolClienteResponsableValidacion', 'isInValidRolresponsablevalidacion')}
                                            disabled={currentFormView === enum_OBLIGATION_FORM_VIEW.VIEW}
                                            checkable
                                            styles={ {title: { height: "27px" }} }
                                            className="mb-1"
                                        />
                                        {
                                            currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                            <div>
                                                <CValidator isInValid={objIsValidForm.isInValidRolresponsablevalidacion} inValidText={t('data-lang-msg-required')} /> 
                                            </div>
                                        }
                                        {
                                            currentFormView !== enum_OBLIGATION_FORM_VIEW.VIEW &&
                                            <div>
                                                <CValidator isInValid={objIsValidForm.isInValidRoles_SameRolesNotAlowed} inValidText={t('data-lang-msg-responsiblerolachievevalidatesamenotalowed')} /> 
                                            </div>
                                        }
                                    </Col>
                                </Row>
                            }
                            
                        </Card>
                    </fieldset>
               }
                <Dialog
                    hidden={!isDialogVisible}
                    onDismiss={onHideDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: t('data-lang-msg-close-panel'),
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } },
                    }}
                    >
                    <DialogFooter>
                        <PrimaryButton onClick={onHideDialogAndPanel} text={t('data-lang-lbl-yes')} />
                        <DefaultButton onClick={onHideDialog} text={t('data-lang-lbl-no')} />
                    </DialogFooter>
                </Dialog>
            </Panel>
        </div>
    );
};

SidePanelAddNew.propTypes = {
    inputParam_IsPanelOpen: PropTypes.bool.isRequired,
    inputParam_SetterIsPanelOpen: PropTypes.func.isRequired,
};

export default SidePanelAddNew;